import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchSongDetail } from "../store/actions/song";
import { ReactComponent as InfoIcon } from '../assets/info-icon.svg';
import { ReactComponent as MusicIcon } from '../assets/music-icon.svg';
import SongLyrics from "../components/SongLyrics";
import ShareButton from "../components/ShareButton";
import '../stylesheets/styles.css'
import AudioPlayer from "../components/AudioPlayer";
import YoutubePlayer from "../components/YoutubePlayer";
import { getUrlExtension } from "../utilities/helper";
import Popup from "reactjs-popup";
import HomeIcon from "../components/icons/HomeIcon";
import BackIcon from "../components/icons/BackIcon";
import GuitarIcon from "../components/icons/GuitarIcon";
import { Helmet } from "react-helmet-async";

/**
 * SongDetailPage Component
 * Displays detailed information about a specific song including:
 * - Song lyrics with optional chord display
 * - Audio/video players
 * - Related songs and tunes
 * - Song information and sheet music
 * - Copyright information
 */
const SongDetailPage = props => {
    // Redux state and navigation
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // State for chord display toggle
    const [ displayChords, setDisplayChords ] = useState(true)

    // Get song details from Redux store and song ID from URL params
    const { songDetail } = useSelector(state => state.song)
    const { songId } = useParams()
    const medias = songDetail?.media ?? []

    // Read branding from runtime config (set in index.js from public/config.json)
    const config = window.__HYMNFORGE_CONFIG__ || {};
    const siteName = config.site_name || 'HymnForge Songs';
    const siteUrl  = config.site_url  || window.location.origin;

    // Canonical URL always points to the primary domain to prevent duplicate-content
    // penalties — all installs read from the same SongStack database, so Google
    // should index only one authoritative copy.
    const canonicalUrl = `https://songs.enjoyer.life/song/${songId}`;

    // Share/OG URL uses the installer's own domain so their links work correctly
    const songUrl = `${siteUrl}/song/${songId}`;

    // Determine song name from available data
    let name = ''
    if ((songDetail?.song_name !== null) && (songDetail?.song_name !== undefined) && (songDetail?.song_name !== '')) {
        name = songDetail?.song_name
    } else if ((songDetail?.song_firstline !== null) && (songDetail?.song_firstline !== undefined) && (songDetail?.song_firstline !== '')) {
        name = songDetail?.song_firstline
    }

    // Set page title and description for SEO
    const pageTitle    = `${songDetail?.song_number ?? ''} - ${name}`;
    const pageDescription = `Enjoy the song "${name}" (${songDetail?.song_number ?? ''}.)`;
    const fullTitle    = `${pageTitle} | ${siteName}`;

    // Fetch song details when component mounts or songId changes
    useEffect(() => {
        dispatch(fetchSongDetail(songId))
    }, [dispatch, songId])

    /**
     * Navigation handlers
     */
    const backIconClick = () => {
        navigate(-1)
    }

    const homeIconClick = () => {
        navigate('/')
    }

    const guitarIconClick = () => {
        console.log('Guitar clicked');
        setDisplayChords(!displayChords)
    }

    // Navigation and control buttons
    const NavButtons = (
        <div className="nav-buttons">
            <button className="nav-icon-button" onClick={homeIconClick} title="Home">
                <HomeIcon className="nav-icon" />
            </button>
            <button className="nav-icon-button" onClick={backIconClick} title="Back">
                <BackIcon className="nav-icon" />
            </button>
        </div>
    )

    const GuitarButton = <button className="guitar-icon-button" onClick={guitarIconClick}>
        <GuitarIcon className="guitar-icon" />
    </button>

    // Related songs and tunes components
    const RelatedSongs = (songDetail?.related_songs?.map((s, i) => (
        <h5 className="related-songs-link" key={`rsng${i}`} onClick={() => navigate(`/detail/${s.song_id}`)}>
            {s.song_number}{(i + 1) === songDetail?.related_songs.length ? '' : ','}
        </h5>
    )))

    const RelatedSongsComponent = RelatedSongs?.length > 0 ? 
        (<div className="related-songs">&#127760;{RelatedSongs}</div>) : 
        <div />

    const RelatedTunes = (songDetail?.related_tunes?.map((s, i) => (
        <h5 className="related-songs-link" key={`rtns${i}`} onClick={() => navigate(`/detail/${s.song_id}`)}>
            {s.tune_name}{(i + 1) === songDetail?.related_tunes.length ? '' : ','}
        </h5>
    )))
    console.log('Relatedtunes:', );
    const RelatedTunesComponent = RelatedTunes?.length > 0 ? 
        (<div className="related-songs">Related tunes: {RelatedTunes}</div>) : 
        <div />

    // Capo and key information
    let text = '';
    if (songDetail?.capo && songDetail?.capo !== '0' && songDetail?.key_with_capo) {
        text = `Capo ${songDetail.capo}\u2013${songDetail.key_with_capo}`;
    } else if (songDetail?.song_key) {
        text = songDetail.song_key;
    }

    const Capo = text ? (<div className="capo-text"><p>{text}</p></div>) : null;

    // Process media components (audio/video players)
    let mediaComponents = []
    for (const media of medias) {
        const mediaUrl = media.media_url
        console.log('MEDIA URL:', mediaUrl);
        console.log(getUrlExtension(mediaUrl));
        if (mediaUrl.includes('youtube.com')) {
            mediaComponents.push((
                <YoutubePlayer 
                    key={media.id} 
                    title={media.title} 
                    className="video-player" 
                    url={mediaUrl} 
                />
            ))
        } else if((getUrlExtension(mediaUrl) === 'mp3') || (getUrlExtension(mediaUrl) === 'm4a')) {
            if (media.allow_download === 1 ) {
                mediaComponents.push((
                    <AudioPlayer 
                        key={media.id} 
                        title={media.title} 
                        className='audio-player' 
                        url={mediaUrl} 
                    />
                ))
            }
        }
    }

    // Information popups
    const SongInfo = (
        <Popup trigger={<div><InfoIcon className="info-icon" /></div>} modal>
            {close => (
                <div className='overlay'>
                    <button className="close" onClick={close}>&times;</button>
                    <div className='popup-content' dangerouslySetInnerHTML={{__html: songDetail?.song_info ?? ''}}></div>
                </div>
            )}
        </Popup>
    )

    const MusicInfo = (
        <Popup trigger={<div><MusicIcon className="info-icon" /></div>} modal>
            {close => (
                <div className='overlay'>
                    <button className="close" onClick={close}>&times;</button>
                    <div className='music-popup-content'>
                        <img className="music-sheet" src={songDetail?.sheet_music_url ?? ''} alt='new'/>
                    </div>
                </div>
            )}
        </Popup>
    )

    const Copyright = (<p className="copyright-text">{songDetail?.copyright_attribution}</p>)

    // Render component
    return songDetail ? (
        <>
            {/* SEO meta tags */}
            <Helmet>
                <title>{fullTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph Tags */}
                <meta property="og:title" content={fullTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={songUrl} />
                <meta property="og:image" content={songDetail?.media?.[0]?.thumbnail || `${siteUrl}/LOGO.png`} />
                <meta property="og:site_name" content={siteName} />

                {/* Twitter Metadata */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={fullTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={songDetail?.media?.[0]?.thumbnail || `${siteUrl}/LOGO.png`} />
            </Helmet>
            
            <div className="detail-body">
                <h1>{pageTitle}</h1>
                {NavButtons}
                <div className="icon-container">
                    {GuitarButton} 
                    <ShareButton url={songUrl} />
                </div>
                {RelatedSongsComponent}
                {RelatedTunesComponent}
                {displayChords && Capo}
                <div className="song-text-body">
                    <SongLyrics lyrics={songDetail?.song_text} showChords={displayChords} />
                    <div className="other-buttons">
                        {SongInfo}
                        {MusicInfo}
                        <ShareButton url={songUrl} />
                    </div>
                    <div className="medias">
                        {mediaComponents.map(m => m)}
                    </div>
                </div>
                {Copyright}
            </div>
        </>
    ) : (
        <div className="loading-text">Loading Songs Data</div>
    );
};

export default SongDetailPage
